import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Alert,
  AlertDescription,
  AlertIcon,
  Badge,
  Box,
  BoxProps,
  Button,
  FormLabel,
  Heading,
  HStack,
  IconButton,
  Input,
  Select,
  Tab,
  Table,
  TableContainer,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  VStack,
} from '@chakra-ui/react';
import { faCircleInfo } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { TitleBar } from '@resistapp/client/components/bars/title-bar';
import { BlandAltmanPlot } from '@resistapp/client/components/plots/bland-altman-plot';
import { GridView } from '@resistapp/client/components/shared/layout';
import { useSearchParamsContext } from '@resistapp/client/contexts/search-params-context';
import { usePostChipReproducibility } from '@resistapp/client/hooks/api';
import { useContainerWidth } from '@resistapp/client/hooks/use-container-width';
import {
  ChipReproducibilityRequest,
  ChipReproducibilityResponse,
  ReproConfidenceInterval,
  ReproPairwiseComparison,
  ReproReport,
  ReproResolvedBlock,
  ReproducibilityAssayScope,
  ReproVerdict,
} from '@resistapp/common/api-types';
import {
  themeQuartz,
  type CellStyle,
  type CellValueChangedEvent,
  type ColDef,
  type ColGroupDef,
  type ProcessDataFromClipboardParams,
  type ValueGetterParams,
} from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  decodeReproState,
  encodeReproState,
  parseLegacyReproParams,
  REPRO_STATE_PARAM,
  ReproUrlState,
} from './reproducibility-url-state';

type InputMode = 'chips' | 'sets';

interface ChipColumn {
  id: string;
  text: string;
}

interface SetColumn {
  id: string;
  label: string;
  chipId?: number | null;
}

// AG Grid row: reserved `id`/`label` plus one string field per set column id.
type GroupRow = Record<string, string>;

// Read-only sample metadata keyed by sample id (intrinsic to the id, independent of which set it sits in).
interface SampleMeta {
  samplingPointName: string | null;
  time: string | null;
}

// Read-only metadata columns rendered next to each set's sample-id column once a result is loaded.
const META_COLS_PER_SET = 2; // sampling point + date
const READONLY_META_CELL_STYLE: CellStyle = { color: '#718096', backgroundColor: '#fafafa' };

let idCounter = 0;
function generateId(prefix: string): string {
  idCounter += 1;
  return `${prefix}${idCounter}`;
}

function parseStrictIntegerText(value: string | undefined): number | null {
  if (value == null) return null;
  const trimmed = value.trim();
  if (trimmed === '' || !/^\d+$/.test(trimmed)) return null;
  const parsed = Number(trimmed);
  return Number.isSafeInteger(parsed) ? parsed : null;
}

function normalizeIntegerCell(value: unknown): string {
  if (typeof value === 'number') {
    return Number.isSafeInteger(value) && value >= 0 ? String(value) : '';
  }
  if (typeof value === 'string') {
    const parsed = parseStrictIntegerText(value);
    return parsed == null ? '' : String(parsed);
  }
  return '';
}

function timeToIsoDate(time: string | null): string {
  if (!time) return '—';
  const d = new Date(time);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toISOString().slice(0, 10);
}

function createEmptyGroupRow(setColumns: SetColumn[]): GroupRow {
  const row: GroupRow = { id: generateId('row'), label: '' };
  setColumns.forEach(col => {
    row[col.id] = '';
  });
  return row;
}

function defaultChipColumns(): ChipColumn[] {
  return [
    { id: generateId('chip'), text: '' },
    { id: generateId('chip'), text: '' },
  ];
}

function defaultSetColumns(): SetColumn[] {
  return [
    { id: generateId('set'), label: 'A' },
    { id: generateId('set'), label: 'B' },
  ];
}

// Sample metadata is a property of the sample id alone, so a single id->meta map serves every set column.
function sampleMetaFromResult(result: ChipReproducibilityResponse | null): Map<number, SampleMeta> {
  const map = new Map<number, SampleMeta>();
  result?.blocks.forEach(block => {
    block.sampleGroups.forEach(group => {
      group.cells.forEach(cell => {
        if (cell.sampleId != null) {
          map.set(cell.sampleId, { samplingPointName: cell.samplingPointName, time: cell.time });
        }
      });
    });
  });
  return map;
}

function metaValueGetter(
  setColId: string,
  kind: 'point' | 'date',
  sampleMeta: Map<number, SampleMeta>,
): (params: ValueGetterParams<GroupRow>) => string {
  return params => {
    const data = params.data;
    if (!data) return '';
    const id = parseStrictIntegerText(data[setColId]);
    if (id == null) return '';
    const meta = sampleMeta.get(id);
    if (!meta) return '';
    return kind === 'point' ? meta.samplingPointName ?? '' : timeToIsoDate(meta.time);
  };
}

function metaColumn(setColId: string, kind: 'point' | 'date', sampleMeta: Map<number, SampleMeta>): ColDef<GroupRow> {
  return {
    headerName: kind === 'point' ? 'Sampling point' : 'Date',
    colId: `${setColId}__${kind}`,
    editable: false,
    sortable: false,
    suppressNavigable: true,
    suppressPaste: true,
    flex: 1,
    minWidth: kind === 'point' ? 140 : 120,
    cellStyle: READONLY_META_CELL_STYLE,
    valueGetter: metaValueGetter(setColId, kind, sampleMeta),
  };
}

// Without a result, one editable id column per set. With a result, each set becomes a header group of
// [editable sample id | read-only sampling point | read-only date].
function buildSetColumnDefs(
  setColumns: SetColumn[],
  sampleMeta: Map<number, SampleMeta>,
  showMeta: boolean,
): Array<ColDef<GroupRow> | ColGroupDef<GroupRow>> {
  return setColumns.map((col, index): ColDef<GroupRow> | ColGroupDef<GroupRow> => {
    const idColumn: ColDef<GroupRow> = {
      headerName: showMeta ? 'Sample id' : col.label || 'Set',
      colId: col.id,
      field: col.id,
      flex: 1,
      minWidth: 110,
      editable: true,
    };
    if (!showMeta) return idColumn;
    return {
      headerName: col.label || `Set ${index + 1}`,
      groupId: `grp_${col.id}`,
      children: [idColumn, metaColumn(col.id, 'point', sampleMeta), metaColumn(col.id, 'date', sampleMeta)],
    };
  });
}

// The grid maps pasted columns positionally across ALL displayed columns, so when the read-only metadata columns are
// present we pad each pasted row with blanks for them, keeping sample ids aligned to each set's id column.
function padClipboardRowsForMetaColumns(rows: string[][]): string[][] {
  return rows.map(row => row.flatMap(cell => [cell, ...Array.from({ length: META_COLS_PER_SET }, () => '')]));
}

export function ReproducibilityView() {
  const { searchParams, setSearchParamsStable } = useSearchParamsContext();

  const [inputMode, setInputMode] = useState<InputMode>('sets');
  const [chipColumns, setChipColumns] = useState<ChipColumn[]>(defaultChipColumns);
  const [setColumns, setSetColumns] = useState<SetColumn[]>(defaultSetColumns);
  const [groupRows, setGroupRows] = useState<GroupRow[]>([]);
  const [assayScope, setAssayScope] = useState<ReproducibilityAssayScope>('all');
  const [result, setResult] = useState<ChipReproducibilityResponse | null>(null);

  const initDoneRef = useRef(false);
  const autoRunDoneRef = useRef(false);
  // Read the current set columns inside the (stable) clipboard handler without making it depend on setColumns.
  const setColumnsRef = useRef(setColumns);
  setColumnsRef.current = setColumns;
  const postRepro = usePostChipReproducibility();

  // Read-only sample metadata is loaded only when a result is present (i.e. on analyse).
  const sampleMeta = useMemo(() => sampleMetaFromResult(result), [result]);
  const showMeta = result != null;
  const showMetaRef = useRef(showMeta);
  showMetaRef.current = showMeta;
  const resolvedBlock = result?.blocks[0];

  // ---------------------------------------------------------------------------------------------------------------
  // URL <-> state
  // ---------------------------------------------------------------------------------------------------------------

  const applyUrlState = useCallback((state: ReproUrlState) => {
    if (state.mode === 'chips') {
      setInputMode('chips');
      setAssayScope('all');
      setChipColumns(
        state.chipIds.length >= 2
          ? state.chipIds.map(id => ({ id: generateId('chip'), text: id != null ? String(id) : '' }))
          : defaultChipColumns(),
      );
      return;
    }
    setInputMode('sets');
    setAssayScope(state.assayScope ?? 'all');
    // A single column set instance is shared between setColumns and the rows so their field ids always match.
    const columns =
      state.setLabels.length >= 2
        ? state.setLabels.map((label, index) => ({
            id: generateId('set'),
            label,
            chipId: state.chipIds?.[index] ?? null,
          }))
        : defaultSetColumns();
    setSetColumns(columns);
    setGroupRows(
      state.groups.length > 0
        ? state.groups.map(group => {
            const row: GroupRow = { id: generateId('row'), label: '' };
            columns.forEach((col, idx) => {
              const value = group[idx];
              row[col.id] = value != null ? String(value) : '';
            });
            return row;
          })
        : [createEmptyGroupRow(columns)],
    );
  }, []);

  useEffect(() => {
    if (initDoneRef.current) return;
    initDoneRef.current = true;

    const compact = decodeReproState(searchParams.get(REPRO_STATE_PARAM));
    const state = compact ?? parseLegacyReproParams(searchParams);
    if (state) {
      applyUrlState(state);
    } else {
      const columns = defaultSetColumns();
      setSetColumns(columns);
      setGroupRows([createEmptyGroupRow(columns)]);
    }
  }, [applyUrlState, searchParams]);

  const currentUrlState = useMemo<ReproUrlState>(() => {
    if (inputMode === 'chips') {
      return { mode: 'chips', chipIds: chipColumns.map(col => parseStrictIntegerText(col.text)) };
    }
    return {
      mode: 'sets',
      setLabels: setColumns.map(col => col.label),
      chipIds: setColumns.some(col => col.chipId != null) ? setColumns.map(col => col.chipId ?? null) : undefined,
      assayScope: assayScope === 'shared' ? 'shared' : undefined,
      groups: groupRows.map(row => setColumns.map(col => parseStrictIntegerText(row[col.id]))),
    };
  }, [assayScope, chipColumns, groupRows, inputMode, setColumns]);

  const syncUrlStable = useCallback(() => {
    const encoded = encodeReproState(currentUrlState);
    setSearchParamsStable(
      prev => {
        const next = new URLSearchParams(prev);
        ['pair', 'mode', 'chipA', 'chipB'].forEach(legacy => {
          next.delete(legacy);
        });
        next.set(REPRO_STATE_PARAM, encoded);
        return next;
      },
      { replace: true },
    );
  }, [currentUrlState, setSearchParamsStable]);

  useEffect(() => {
    if (!initDoneRef.current) return;
    syncUrlStable();
  }, [syncUrlStable]);

  // ---------------------------------------------------------------------------------------------------------------
  // Mutators
  // ---------------------------------------------------------------------------------------------------------------

  const clearResult = useCallback(() => {
    setResult(null);
  }, []);

  const setInputModeStable = useCallback(
    (mode: InputMode) => {
      setInputMode(mode);
      clearResult();
    },
    [clearResult],
  );

  const updateChipText = useCallback(
    (id: string, text: string) => {
      setChipColumns(prev => prev.map(col => (col.id === id ? { ...col, text } : col)));
      clearResult();
    },
    [clearResult],
  );

  const addChipColumn = useCallback(() => {
    setChipColumns(prev => [...prev, { id: generateId('chip'), text: '' }]);
    clearResult();
  }, [clearResult]);

  const removeChipColumn = useCallback(
    (id: string) => {
      setChipColumns(prev => (prev.length <= 2 ? prev : prev.filter(col => col.id !== id)));
      clearResult();
    },
    [clearResult],
  );

  const moveChipColumn = useCallback(
    (id: string, direction: -1 | 1) => {
      setChipColumns(prev => moveItem(prev, col => col.id === id, direction));
      clearResult();
    },
    [clearResult],
  );

  const renameSetColumn = useCallback(
    (id: string, label: string) => {
      setSetColumns(prev => prev.map(col => (col.id === id ? { ...col, label } : col)));
      clearResult();
    },
    [clearResult],
  );

  const updateAssayScope = useCallback(
    (scope: ReproducibilityAssayScope) => {
      setAssayScope(scope);
      clearResult();
    },
    [clearResult],
  );

  const addSetColumn = useCallback(() => {
    const newColumn: SetColumn = { id: generateId('set'), label: nextSetLabel(setColumns) };
    setSetColumns(prev => [...prev, newColumn]);
    setGroupRows(prev => prev.map(row => ({ ...row, [newColumn.id]: '' })));
    clearResult();
  }, [clearResult, setColumns]);

  const removeSetColumn = useCallback(
    (id: string) => {
      setSetColumns(prev => (prev.length <= 2 ? prev : prev.filter(col => col.id !== id)));
      clearResult();
    },
    [clearResult],
  );

  const moveSetColumn = useCallback(
    (id: string, direction: -1 | 1) => {
      setSetColumns(prev => moveItem(prev, col => col.id === id, direction));
      clearResult();
    },
    [clearResult],
  );

  const addGroupRow = useCallback(() => {
    setGroupRows(prev => [...prev, createEmptyGroupRow(setColumns)]);
    clearResult();
  }, [clearResult, setColumns]);

  const removeLastGroupRow = useCallback(() => {
    setGroupRows(prev => (prev.length <= 1 ? prev : prev.slice(0, -1)));
    clearResult();
  }, [clearResult]);

  // ---------------------------------------------------------------------------------------------------------------
  // AG Grid (sets mode)
  // ---------------------------------------------------------------------------------------------------------------

  const columnDefs = useMemo<Array<ColDef<GroupRow> | ColGroupDef<GroupRow>>>(
    () => buildSetColumnDefs(setColumns, sampleMeta, showMeta),
    [setColumns, sampleMeta, showMeta],
  );

  const defaultColDef = useMemo<ColDef<GroupRow>>(
    () => ({ editable: true, resizable: true, sortable: false, filter: false }),
    [],
  );

  const handleCellValueChangedStable = useCallback((event: CellValueChangedEvent<GroupRow>) => {
    const colId = event.column.getColId();
    const rowId = event.data.id;
    const value = colId === 'label' ? String(event.newValue ?? '') : normalizeIntegerCell(event.newValue);
    setGroupRows(prev => prev.map(row => (row.id === rowId ? { ...row, [colId]: value } : row)));
    setResult(null);
  }, []);

  const processDataFromClipboardStable = useCallback((params: ProcessDataFromClipboardParams) => {
    // Pad for the read-only metadata columns while they are displayed so pasted ids stay aligned to each set.
    const data = showMetaRef.current ? padClipboardRowsForMetaColumns(params.data) : params.data;
    const focusedRowIndex = params.api.getFocusedCell()?.rowIndex ?? 0;
    const requiredRows = focusedRowIndex + data.length;
    const currentRowCount = params.api.getDisplayedRowCount();
    const rowsToAddCount = Math.max(requiredRows - currentRowCount, 0);

    if (rowsToAddCount > 0) {
      const rowsToAdd = Array.from({ length: rowsToAddCount }, () => createEmptyGroupRow(setColumnsRef.current));
      params.api.applyTransaction({ add: rowsToAdd });
      setGroupRows(prev => [...prev, ...rowsToAdd]);
    }

    setResult(null);
    return data;
  }, []);

  // ---------------------------------------------------------------------------------------------------------------
  // Analyse
  // ---------------------------------------------------------------------------------------------------------------

  const canAnalyse = useMemo(() => {
    if (inputMode === 'chips') {
      const ids = chipColumns.map(col => parseStrictIntegerText(col.text)).filter((id): id is number => id != null);
      return new Set(ids).size >= 2;
    }
    if (setColumns.length < 2) return false;
    const usableRows = groupRows.filter(
      row => setColumns.filter(col => parseStrictIntegerText(row[col.id]) != null).length >= 2,
    );
    return usableRows.length >= 2;
  }, [chipColumns, groupRows, inputMode, setColumns]);

  const buildRequest = useCallback((): ChipReproducibilityRequest | null => {
    if (inputMode === 'chips') {
      const ids = chipColumns.map(col => parseStrictIntegerText(col.text)).filter((id): id is number => id != null);
      if (new Set(ids).size < 2) return null;
      return { chipIds: ids };
    }
    const sampleGroups = groupRows
      .map((row, index) => ({
        label: `Sample ${index + 1}`,
        sampleIds: setColumns.map(col => parseStrictIntegerText(row[col.id])),
      }))
      .filter(group => group.sampleIds.some(id => id != null));
    if (sampleGroups.length === 0) return null;
    return {
      blocks: [
        {
          label: 'Block 1',
          setLabels: setColumns.map((col, index) => col.label.trim() || `Set ${index + 1}`),
          chipIds: setColumns.every(col => typeof col.chipId === 'number' && Number.isFinite(col.chipId))
            ? (setColumns.map(col => col.chipId) as number[])
            : undefined,
          assayScope,
          sampleGroups,
        },
      ],
    };
  }, [assayScope, chipColumns, groupRows, inputMode, setColumns]);

  const handleAnalyseStable = useCallback(() => {
    setResult(null);
    const body = buildRequest();
    if (!body) return;
    postRepro.mutate(body, {
      onSuccess: data => {
        setResult(data);
      },
    });
  }, [buildRequest, postRepro]);

  useEffect(() => {
    if (!initDoneRef.current || autoRunDoneRef.current || result != null || !canAnalyse) return;
    autoRunDoneRef.current = true;
    handleAnalyseStable();
  }, [canAnalyse, handleAnalyseStable, result]);

  return (
    <>
      <TitleBar title={<span>Reproducibility</span>} />
      <GridView style={{ paddingTop: '25px', paddingBottom: '30px' }}>
        <VStack align="stretch" spacing={6}>
          <SectionCard>
            <Heading as="h3" size="md" mb={4}>
              Input
            </Heading>
            <FormLabel>Input mode</FormLabel>
            <Select
              value={inputMode}
              onChange={event => {
                setInputModeStable(event.target.value === 'chips' ? 'chips' : 'sets');
              }}
              maxW="460px"
            >
              <option value="sets">Sample id sets (one column per run/chip/method)</option>
              <option value="chips">Chip ids (auto-pair samples by sorted sample id)</option>
            </Select>

            {inputMode === 'chips' ? (
              <>
                <ChipInputs
                  chipColumns={chipColumns}
                  onChange={updateChipText}
                  onAdd={addChipColumn}
                  onRemove={removeChipColumn}
                  onMove={moveChipColumn}
                />
                {resolvedBlock && (
                  <Box mt={6}>
                    <FormLabel>Resolved samples</FormLabel>
                    <Text fontSize="sm" color="gray.600" mb={2}>
                      Auto-paired samples per chip, loaded on analyse (read-only).
                    </Text>
                    <ResolvedSamplesGrid block={resolvedBlock} />
                  </Box>
                )}
              </>
            ) : (
              <>
                <FormLabel mt={4}>Assay scope</FormLabel>
                <Select
                  value={assayScope}
                  onChange={event => {
                    updateAssayScope(event.target.value === 'shared' ? 'shared' : 'all');
                  }}
                  maxW="460px"
                >
                  <option value="all">All assays in the selected sets</option>
                  <option value="shared">Only assays shared within each sample row</option>
                </Select>
                <SetInputs
                  setColumns={setColumns}
                  groupRows={groupRows}
                  columnDefs={columnDefs}
                  defaultColDef={defaultColDef}
                  onRename={renameSetColumn}
                  onAddSet={addSetColumn}
                  onRemoveSet={removeSetColumn}
                  onMoveSet={moveSetColumn}
                  onAddRow={addGroupRow}
                  onRemoveRow={removeLastGroupRow}
                  onCellValueChanged={handleCellValueChangedStable}
                  onProcessClipboard={processDataFromClipboardStable}
                />
              </>
            )}

            <Box mt={4}>
              <Button
                colorScheme="green"
                onClick={handleAnalyseStable}
                isDisabled={!canAnalyse}
                isLoading={postRepro.isPending}
              >
                Analyse
              </Button>
            </Box>
            {postRepro.isError && (
              <Alert status="error" borderRadius="md" mt={4}>
                <AlertIcon />
                <AlertDescription>{postRepro.error.message}</AlertDescription>
              </Alert>
            )}
          </SectionCard>

          {result ? (
            <ReproducibilityResults result={result} />
          ) : (
            <Box
              borderWidth="1px"
              borderStyle="dashed"
              borderColor="neutral.300"
              borderRadius="lg"
              py={10}
              textAlign="center"
            >
              <Text color="gray.500">Analyse to see the overall decision and pairwise comparisons.</Text>
            </Box>
          )}
        </VStack>
      </GridView>
    </>
  );
}

// ---------------------------------------------------------------------------------------------------------------------
// Shared presentation helpers
// ---------------------------------------------------------------------------------------------------------------------

// First-principles metric definition rendered inside a hover info icon. Fields are shown in order:
// what it is → which observations are counted → what it represents → which direction is good → its decision role.
interface MetricInfo {
  definition: string;
  scope?: string;
  represents?: string;
  interpretation?: string;
  role: string;
}

function metricTooltip(info: MetricInfo): React.ReactNode {
  return (
    <VStack align="stretch" spacing={1}>
      <Text fontWeight="bold">{info.definition}</Text>
      {info.scope && <Text color="whiteAlpha.800">{info.scope}</Text>}
      {info.represents && <Text color="whiteAlpha.800">{info.represents}</Text>}
      {info.interpretation && <Text color="whiteAlpha.800">{info.interpretation}</Text>}
      <Text fontWeight="semibold" pt={0.5}>
        {info.role}
      </Text>
    </VStack>
  );
}

const CONSENSUS_MAD_INFO_OVERALL = metricTooltip({
  definition:
    'Mean absolute deviation of each set from the per-point consensus Cq (the mean of the valid values at a sample-assay point).',
  scope:
    'Over points with ≥2 valid values and ≥1 quantified result; points where every set is a non-detection are excluded.',
  represents: 'Total quantitative disagreement — systematic bias between sets plus random scatter.',
  interpretation: 'Low means the sets agree closely on Cq.',
  role: 'Primary quantitative decision metric.',
});

const CONSENSUS_MAD_INFO_PAIR = metricTooltip({
  definition:
    'Mean absolute deviation of sample-assays from their pair mean (the average of A and B); equals half the mean |ΔCq|.',
  scope: 'Over sample-assays where both runs are valid and at least one is quantified.',
  represents: 'Both systematic bias and random disagreement between the two runs.',
  interpretation: 'Low means A and B agree closely.',
  role: 'Primary quantitative decision metric.',
});

const QA_MISMATCH_INFO_OVERALL = metricTooltip({
  definition:
    'Share of sample-assays where the sets disagree on detection — one gives a valid result while another fails QA or is missing.',
  scope: 'Counted pairwise-equivalently: valid × failed comparisons over all set pairs.',
  represents: 'Qualitative (detect vs non-detect) disagreement, independent of Cq magnitude.',
  interpretation: 'Low means the sets agree on what is detectable.',
  role: 'Primary qualitative decision metric.',
});

const QA_MISMATCH_INFO_PAIR = metricTooltip({
  definition: 'Share of sample-assays where one run gives a valid result and the other fails QA or is missing.',
  scope: 'Out of all sample-assays compared between the two runs.',
  represents: 'Qualitative (detect vs non-detect) disagreement, independent of Cq magnitude.',
  interpretation: 'Low means A and B agree on detect vs non-detect.',
  role: 'Primary qualitative decision metric.',
});

const HETEROGENEITY_INFO = metricTooltip({
  definition: 'Checks whether any single set disagrees with the rest more than expected.',
  scope: 'Combines the spread of per-set MADs (quantitative) with per-set QA mismatch (qualitative).',
  represents: 'Catches one bad run or method that the pooled averages would otherwise hide.',
  interpretation: 'A flagged set can fail the overall result even when the pooled metrics pass.',
  role: 'Decision gate alongside the pooled metrics.',
});

const SIGNED_SHIFT_INFO = metricTooltip({
  definition: "Mean signed deviation of the set's values from the per-point consensus, keeping its sign.",
  represents: 'Systematic bias / direction — whether the set reads consistently high or low.',
  interpretation: 'Near zero means no offset; a large ± means a consistent shift.',
  role: 'Heterogeneity diagnostic (a strong shift can flag a set).',
});

const SAMPLE_PAIRS_INFO = metricTooltip({
  definition: 'Number of conceptual samples compared between the two runs (n).',
  scope: 'The sub-count is the underlying sample × assay observations behind them.',
  interpretation: 'More pairs give tighter confidence intervals.',
  role: 'Context / evidence size — not a pass/fail metric.',
});

const AGREED_NON_DETECTION_INFO_PAIR = metricTooltip({
  definition:
    'Share of comparable sample-assays where both runs agree the target is a clean non-detection (below the limit of quantification).',
  represents: 'Agreement on absence of the target.',
  role: 'Context only — deliberately excluded from the pass/fail rule so easy agreed zeros do not inflate agreement.',
});

const MATCHED_NON_DETECTION_INFO_OVERALL = metricTooltip({
  definition:
    'Share of comparable sample-assays where every contributing set agrees the target is a clean non-detection (below the limit of quantification).',
  represents: 'Agreement on absence of the target.',
  role: 'Context only — deliberately excluded from the pass/fail rule so easy agreed zeros do not inflate agreement.',
});

const LOA_WIDTH_INFO = metricTooltip({
  definition: 'Width of the 95% Bland–Altman limits of agreement = 2 × 1.96 × SD(ΔCq).',
  scope: 'From the spread of ΔCq across the compared sample-assays.',
  represents: 'The band containing 95% of pairwise differences — the random scatter, after removing mean bias.',
  interpretation: 'Narrow means tight agreement.',
  role: 'Diagnostic only — not a decision metric.',
});

function SectionCard({ children, ...rest }: BoxProps) {
  return (
    <Box bg="white" borderWidth="1px" borderColor="neutral.200" borderRadius="lg" boxShadow="sm" p={5} {...rest}>
      {children}
    </Box>
  );
}

// Small hover tooltip used to move long metric definitions off the page and behind a discreet info icon.
function InfoTip({ label }: { label: React.ReactNode }) {
  return (
    <Tooltip label={label} hasArrow placement="top" maxW="320px" borderRadius="md" px={3} py={2} fontSize="xs">
      <Box
        as="span"
        display="inline-flex"
        alignItems="center"
        ml={1.5}
        color="gray.400"
        cursor="help"
        _hover={{ color: 'gray.600' }}
        aria-label="More information"
      >
        <FontAwesomeIcon icon={faCircleInfo} size="sm" />
      </Box>
    </Tooltip>
  );
}

// Clearly-bordered disclosure card so collapsible sections read as interactive instead of blending into the page.
function CollapsibleSection({
  title,
  badge,
  children,
}: {
  title: string;
  badge?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <Accordion allowToggle>
      <AccordionItem border="1px solid" borderColor="neutral.200" borderRadius="lg" overflow="hidden" bg="white">
        <AccordionButton px={4} py={3} bg="neutral.50" _hover={{ bg: 'neutral.100' }} _expanded={{ bg: 'neutral.100' }}>
          <HStack flex="1" spacing={2} textAlign="left">
            <Heading as="h4" size="sm">
              {title}
            </Heading>
            {badge}
          </HStack>
          <AccordionIcon />
        </AccordionButton>
        <AccordionPanel px={4} py={4}>
          {children}
        </AccordionPanel>
      </AccordionItem>
    </Accordion>
  );
}

// ---------------------------------------------------------------------------------------------------------------------
// Input sub-components
// ---------------------------------------------------------------------------------------------------------------------

interface ChipInputsProps {
  chipColumns: ChipColumn[];
  onChange: (id: string, text: string) => void;
  onAdd: () => void;
  onRemove: (id: string) => void;
  onMove: (id: string, direction: -1 | 1) => void;
}

function ChipInputs({ chipColumns, onChange, onAdd, onRemove, onMove }: ChipInputsProps) {
  return (
    <Box mt={4} maxW="640px">
      <FormLabel>Chips</FormLabel>
      <VStack align="stretch" spacing={2}>
        {chipColumns.map((col, index) => (
          <HStack key={col.id} spacing={2}>
            <Text minW="64px" color="gray.600" fontSize="sm">
              Set {index + 1}
            </Text>
            <Input
              type="number"
              value={col.text}
              onChange={event => {
                onChange(col.id, event.target.value);
              }}
              placeholder={`Chip id ${index + 1}`}
            />
            <IconButton
              aria-label="Move up"
              size="sm"
              variant="ghost"
              isDisabled={index === 0}
              onClick={() => {
                onMove(col.id, -1);
              }}
              icon={<span>↑</span>}
            />
            <IconButton
              aria-label="Move down"
              size="sm"
              variant="ghost"
              isDisabled={index === chipColumns.length - 1}
              onClick={() => {
                onMove(col.id, 1);
              }}
              icon={<span>↓</span>}
            />
            <IconButton
              aria-label="Remove chip"
              size="sm"
              variant="ghost"
              colorScheme="red"
              isDisabled={chipColumns.length <= 2}
              onClick={() => {
                onRemove(col.id);
              }}
              icon={<span>✕</span>}
            />
          </HStack>
        ))}
      </VStack>
      <HStack mt={3}>
        <Button size="sm" onClick={onAdd}>
          Add chip
        </Button>
      </HStack>
      <Text fontSize="sm" color="gray.600" mt={2}>
        Pairs samples by sorted sample id across all chips. Assumes every chip has the same number of samples.
      </Text>
    </Box>
  );
}

interface SetInputsProps {
  setColumns: SetColumn[];
  groupRows: GroupRow[];
  columnDefs: Array<ColDef<GroupRow> | ColGroupDef<GroupRow>>;
  defaultColDef: ColDef<GroupRow>;
  onRename: (id: string, label: string) => void;
  onAddSet: () => void;
  onRemoveSet: (id: string) => void;
  onMoveSet: (id: string, direction: -1 | 1) => void;
  onAddRow: () => void;
  onRemoveRow: () => void;
  onCellValueChanged: (event: CellValueChangedEvent<GroupRow>) => void;
  onProcessClipboard: (params: ProcessDataFromClipboardParams) => string[][];
}

function SetInputs({
  setColumns,
  groupRows,
  columnDefs,
  defaultColDef,
  onRename,
  onAddSet,
  onRemoveSet,
  onMoveSet,
  onAddRow,
  onRemoveRow,
  onCellValueChanged,
  onProcessClipboard,
}: SetInputsProps) {
  return (
    <Box mt={4}>
      <FormLabel>Sets</FormLabel>
      <Text fontSize="sm" color="gray.600" mb={3}>
        Each set is one run, chip, method or column. Name the sets, then paste sample ids from a spreadsheet (one column
        per set, one row per conceptual sample).
      </Text>
      <Box overflowX="auto">
        <HStack spacing={3} align="flex-start" mb={3} minW="min-content">
          {setColumns.map((col, index) => (
            <VStack key={col.id} align="stretch" spacing={1} minW="160px">
              <Input
                size="sm"
                value={col.label}
                onChange={event => {
                  onRename(col.id, event.target.value);
                }}
                placeholder={`Set ${index + 1}`}
              />
              <HStack spacing={1}>
                <IconButton
                  aria-label="Move set left"
                  size="xs"
                  variant="ghost"
                  isDisabled={index === 0}
                  onClick={() => {
                    onMoveSet(col.id, -1);
                  }}
                  icon={<span>←</span>}
                />
                <IconButton
                  aria-label="Move set right"
                  size="xs"
                  variant="ghost"
                  isDisabled={index === setColumns.length - 1}
                  onClick={() => {
                    onMoveSet(col.id, 1);
                  }}
                  icon={<span>→</span>}
                />
                <IconButton
                  aria-label="Remove set"
                  size="xs"
                  variant="ghost"
                  colorScheme="red"
                  isDisabled={setColumns.length <= 2}
                  onClick={() => {
                    onRemoveSet(col.id);
                  }}
                  icon={<span>✕</span>}
                />
              </HStack>
            </VStack>
          ))}
          <Button size="sm" onClick={onAddSet} mt={0}>
            Add set
          </Button>
        </HStack>
      </Box>
      <Box borderWidth="1px" borderRadius="md" overflow="hidden">
        <Box style={{ width: '100%', height: '340px' }}>
          <AgGridReact<GroupRow>
            rowData={groupRows}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            onCellValueChanged={onCellValueChanged}
            processDataFromClipboard={onProcessClipboard}
            getRowId={params => params.data.id}
            theme={themeQuartz}
            rowHeight={30}
            headerHeight={36}
            singleClickEdit
            stopEditingWhenCellsLoseFocus
            suppressContextMenu
          />
        </Box>
      </Box>
      <HStack mt={3} spacing={3}>
        <Button onClick={onAddRow}>Add sample row</Button>
        <Button onClick={onRemoveRow} variant="ghost" isDisabled={groupRows.length <= 1}>
          Remove last row
        </Button>
      </HStack>
    </Box>
  );
}

// ---------------------------------------------------------------------------------------------------------------------
// Results
// ---------------------------------------------------------------------------------------------------------------------

const TAB_STYLE = {
  whiteSpace: 'nowrap' as const,
  fontSize: 'sm',
  fontWeight: 500,
  color: 'gray.600',
  borderRadius: 'md',
  px: 4,
  py: 2,
  _hover: { color: 'gray.900' },
  _selected: { bg: 'white', color: 'neutral.900', fontWeight: 600, boxShadow: 'sm' },
};

function ReproducibilityResults({ result }: { result: ChipReproducibilityResponse }) {
  return (
    <Tabs variant="unstyled" isLazy>
      <Box overflowX="auto" overflowY="hidden" pb={1}>
        <TabList bg="neutral.100" borderRadius="lg" p="5px" gap="4px" width="max-content" minW="100%">
          <Tab {...TAB_STYLE}>Overall report</Tab>
          {result.pairwise.map(pair => (
            <Tab key={`${pair.blockIndex}-${pair.setIndexA}-${pair.setIndexB}`} {...TAB_STYLE}>
              {pair.labelA} vs {pair.labelB}
            </Tab>
          ))}
        </TabList>
      </Box>
      <TabPanels>
        <TabPanel px={0} pt={5}>
          <OverallReport report={result.report} />
        </TabPanel>
        {result.pairwise.map(pair => (
          <TabPanel key={`${pair.blockIndex}-${pair.setIndexA}-${pair.setIndexB}`} px={0} pt={5}>
            <PairwisePanel pair={pair} />
          </TabPanel>
        ))}
      </TabPanels>
    </Tabs>
  );
}

const VERDICT_COLOR: Record<ReproVerdict, string> = { PASS: 'green', INCONCLUSIVE: 'yellow', FAIL: 'red' };

function VerdictBadge({ verdict }: { verdict: ReproVerdict }) {
  return (
    <Badge colorScheme={VERDICT_COLOR[verdict]} fontSize="0.8em">
      {verdict}
    </Badge>
  );
}

function formatCi(ci: ReproConfidenceInterval, digits: number, suffix = ''): string {
  return `${ci.point.toFixed(digits)}${suffix} (95% CI ${ci.lower.toFixed(digits)}–${ci.upper.toFixed(digits)}${suffix})`;
}

function OverallReport({ report }: { report: ReproReport }) {
  const t = report.thresholds;
  return (
    <VStack align="stretch" spacing={6}>
      <Alert
        status={report.conclusion === 'PASS' ? 'success' : report.conclusion === 'FAIL' ? 'error' : 'warning'}
        borderRadius="md"
      >
        <AlertIcon />
        <Box>
          <HStack>
            <Heading as="h3" size="md">
              Conclusion
            </Heading>
            <VerdictBadge verdict={report.conclusion} />
          </HStack>
          <Box mt={2}>
            {report.reasons.map((reason, idx) => (
              <Text key={idx} fontSize="sm">
                • {reason}
              </Text>
            ))}
          </Box>
        </Box>
      </Alert>

      <SectionCard>
        <Heading as="h4" size="sm" mb={3}>
          Pooled metrics
        </Heading>
        <TableContainer borderWidth="1px" borderColor="neutral.200" borderRadius="md">
          <Table size="sm">
            <Thead bg="neutral.50">
              <Tr>
                <Th borderColor="neutral.200">Metric</Th>
                <Th borderColor="neutral.200">Value</Th>
                <Th borderColor="neutral.200">Verdict</Th>
              </Tr>
            </Thead>
            <Tbody>
              <Tr>
                <Td borderColor="neutral.100">
                  <HStack spacing={0}>
                    <Text as="span">Consensus MAD</Text>
                    <InfoTip label={CONSENSUS_MAD_INFO_OVERALL} />
                  </HStack>
                </Td>
                <Td borderColor="neutral.100">
                  {formatCi(report.pooledConsensusMad, 2, ' Cq')}{' '}
                  <Text as="span" color="gray.500" fontSize="xs">
                    · matched non-detection {report.matchedNonDetectionPercent.toFixed(1)}%
                    <InfoTip label={MATCHED_NON_DETECTION_INFO_OVERALL} />
                  </Text>
                </Td>
                <Td borderColor="neutral.100">
                  <VerdictBadge verdict={report.quantVerdict} />
                </Td>
              </Tr>
              <Tr>
                <Td borderColor="neutral.100">
                  <HStack spacing={0}>
                    <Text as="span">QA mismatch</Text>
                    <InfoTip label={QA_MISMATCH_INFO_OVERALL} />
                  </HStack>
                </Td>
                <Td borderColor="neutral.100">{formatCi(report.pooledQaMismatchPercent, 1, '%')}</Td>
                <Td borderColor="neutral.100">
                  <VerdictBadge verdict={report.qaVerdict} />
                </Td>
              </Tr>
              <Tr>
                <Td borderColor="transparent">
                  <HStack spacing={0}>
                    <Text as="span">Heterogeneity (set spread / QA)</Text>
                    <InfoTip label={HETEROGENEITY_INFO} />
                  </HStack>
                </Td>
                <Td borderColor="transparent">
                  worst-block set-MAD spread; {report.evidence.quantObsCount} quantitative observations,{' '}
                  {report.evidence.qaPointCount} QA points
                </Td>
                <Td borderColor="transparent">
                  <VerdictBadge verdict={report.heterogeneityVerdict} />
                </Td>
              </Tr>
            </Tbody>
          </Table>
        </TableContainer>
      </SectionCard>

      {report.blocks.map(block => (
        <SectionCard key={block.blockIndex}>
          <HStack mb={1} spacing={2}>
            <Heading as="h4" size="sm">
              {block.label}: by-set heterogeneity
            </Heading>
            <InfoTip label={HETEROGENEITY_INFO} />
            <VerdictBadge verdict={block.heterogeneityVerdict} />
          </HStack>
          <Text fontSize="xs" color="gray.500" mb={3}>
            Set-MAD spread {formatCi(block.setMadSpread, 2, ' Cq')}; max set MAD {block.maxSetMad.toFixed(2)} Cq.
          </Text>
          <TableContainer borderWidth="1px" borderColor="neutral.200" borderRadius="md">
            <Table size="sm">
              <Thead bg="neutral.50">
                <Tr>
                  <Th borderColor="neutral.200">Set</Th>
                  <Th borderColor="neutral.200">MAD (Cq)</Th>
                  <Th borderColor="neutral.200">
                    <HStack spacing={0}>
                      <Text as="span">Signed shift (Cq)</Text>
                      <InfoTip label={SIGNED_SHIFT_INFO} />
                    </HStack>
                  </Th>
                  <Th borderColor="neutral.200">QA mismatch</Th>
                  <Th borderColor="neutral.200" isNumeric>
                    Quant obs
                  </Th>
                  <Th borderColor="neutral.200">Flag</Th>
                </Tr>
              </Thead>
              <Tbody>
                {block.sets.map(set => (
                  <Tr key={set.setIndex} bg={set.flaggedHeterogeneous ? 'red.50' : undefined}>
                    <Td borderColor="neutral.100" maxW="220px" whiteSpace="normal" wordBreak="break-word">
                      {set.label}
                    </Td>
                    <Td borderColor="neutral.100">{formatCi(set.mad, 2)}</Td>
                    <Td borderColor="neutral.100">{formatCi(set.signedShift, 2)}</Td>
                    <Td borderColor="neutral.100">{formatCi(set.qaMismatchPercent, 1, '%')}</Td>
                    <Td borderColor="neutral.100" isNumeric>
                      {set.quantObsCount}
                    </Td>
                    <Td borderColor="neutral.100">
                      {set.flaggedHeterogeneous ? (
                        <Tooltip label={set.heterogeneityReasons.join('; ')}>
                          <Badge colorScheme="red">Heterogeneous</Badge>
                        </Tooltip>
                      ) : (
                        <Text color="gray.400" fontSize="xs">
                          ok
                        </Text>
                      )}
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TableContainer>
        </SectionCard>
      ))}

      <WorstDiagnostics report={report} />

      <CollapsibleSection title="Decision thresholds (provisional)">
        <TableContainer borderWidth="1px" borderColor="neutral.200" borderRadius="md">
          <Table size="sm">
            <Tbody>
              <ThresholdRow
                label="Quantification / clean non-detection Cq (default; stricter per-assay LOQ applies)"
                value={`${t.cqQuantificationThreshold}`}
              />
              <ThresholdRow label="Consensus MAD PASS (upper CI ≤)" value={`${t.madPassUpperCi} Cq`} />
              <ThresholdRow
                label="Consensus MAD FAIL (point > / lower CI >)"
                value={`${t.madFailPoint} / ${t.madFailLowerCi} Cq`}
              />
              <ThresholdRow label="QA mismatch PASS (upper CI ≤)" value={`${t.qaPassUpperCi}%`} />
              <ThresholdRow
                label="QA mismatch FAIL (point ≥ / lower CI >)"
                value={`${t.qaFailPoint}% / ${t.qaFailLowerCi}%`}
              />
              <ThresholdRow
                label="Set QA failure (point ≥ / lower CI >)"
                value={`${t.setQaFailPoint}% / ${t.setQaFailLowerCi}%`}
              />
              <ThresholdRow label="Set-MAD spread PASS (upper CI ≤)" value={`${t.spreadPassUpperCi} Cq`} />
              <ThresholdRow
                label="Set-MAD spread FAIL (lower CI > / max MAD >)"
                value={`${t.spreadFailLowerCi} / ${t.setMadFailMax} Cq`}
              />
              <ThresholdRow
                label="Evidence floor (sets / sample groups / global quant obs)"
                value={`${t.minSets} / ${t.minSampleGroups} / ${t.minGlobalQuantObs}`}
              />
            </Tbody>
          </Table>
        </TableContainer>
        <Text fontSize="xs" color="gray.500" mt={2}>
          These thresholds are provisional and must be calibrated against known good and known bad studies.
        </Text>
      </CollapsibleSection>
    </VStack>
  );
}

function ThresholdRow({ label, value }: { label: string; value: string }) {
  return (
    <Tr>
      <Td>{label}</Td>
      <Td isNumeric>{value}</Td>
    </Tr>
  );
}

function WorstDiagnostics({ report }: { report: ReproReport }) {
  if (report.worstSamples.length === 0 && report.worstAssays.length === 0) return null;
  return (
    <CollapsibleSection title="Worst samples and assays">
      <HStack align="flex-start" spacing={6} flexWrap="wrap">
        <Box flex="1" minW="300px">
          <Text fontWeight="bold" fontSize="sm" mb={2}>
            Samples
          </Text>
          <TableContainer borderWidth="1px" borderColor="neutral.200" borderRadius="md">
            <Table size="sm">
              <Thead bg="neutral.50">
                <Tr>
                  <Th borderColor="neutral.200">Sample</Th>
                  <Th borderColor="neutral.200" isNumeric>
                    MAD
                  </Th>
                  <Th borderColor="neutral.200" isNumeric>
                    QA
                  </Th>
                </Tr>
              </Thead>
              <Tbody>
                {report.worstSamples.map(sample => (
                  <Tr key={`${sample.blockIndex}-${sample.sampleGroupIndex}`}>
                    <Td borderColor="neutral.100">{sample.label}</Td>
                    <Td borderColor="neutral.100" isNumeric>
                      {sample.consensusMad.toFixed(2)}
                    </Td>
                    <Td borderColor="neutral.100" isNumeric>
                      {sample.qaMismatchPercent.toFixed(1)}%
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TableContainer>
        </Box>
        <Box flex="1" minW="300px">
          <Text fontWeight="bold" fontSize="sm" mb={2}>
            Assays
          </Text>
          <TableContainer borderWidth="1px" borderColor="neutral.200" borderRadius="md">
            <Table size="sm">
              <Thead bg="neutral.50">
                <Tr>
                  <Th borderColor="neutral.200">Assay</Th>
                  <Th borderColor="neutral.200" isNumeric>
                    MAD
                  </Th>
                  <Th borderColor="neutral.200" isNumeric>
                    QA
                  </Th>
                </Tr>
              </Thead>
              <Tbody>
                {report.worstAssays.map(assay => (
                  <Tr key={assay.assay}>
                    <Td borderColor="neutral.100">{assay.assay}</Td>
                    <Td borderColor="neutral.100" isNumeric>
                      {assay.consensusMad.toFixed(2)}
                    </Td>
                    <Td borderColor="neutral.100" isNumeric>
                      {assay.qaMismatchPercent.toFixed(1)}%
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TableContainer>
        </Box>
      </HStack>
    </CollapsibleSection>
  );
}

function StatBox({
  label,
  value,
  sub,
  muted,
  info,
}: {
  label: string;
  value: string;
  sub: string;
  muted?: boolean;
  info?: React.ReactNode;
}) {
  return (
    <Box minW="160px" px={4} py={3} borderWidth="1px" borderColor="neutral.200" borderRadius="md" bg="white">
      <HStack spacing={0} mb={1}>
        <Text fontSize="xs" color="gray.600" textTransform="uppercase" letterSpacing="wide">
          {label}
        </Text>
        {info && <InfoTip label={info} />}
      </HStack>
      <Text fontSize="2xl" fontWeight="bold" lineHeight="1.1" color={muted ? 'gray.400' : 'neutral.900'}>
        {value}
      </Text>
      <Text fontSize="xs" color="gray.500" mt={1}>
        {sub}
      </Text>
    </Box>
  );
}

function PairwisePanel({ pair }: { pair: ReproPairwiseComparison }) {
  const plotContainerRef = useRef<HTMLDivElement>(null);
  const containerWidth = useContainerWidth(plotContainerRef);
  const { blandAltman } = pair;

  return (
    <VStack align="stretch" spacing={6}>
      <SectionCard>
        <Heading as="h4" size="sm" mb={4}>
          {pair.labelA} vs {pair.labelB}
        </Heading>
        <HStack spacing={4} flexWrap="wrap" align="flex-start">
          <StatBox
            label="Sample pairs"
            value={String(pair.n)}
            sub={`${pair.totalSampleAssayPairs} sample-assay`}
            muted
            info={SAMPLE_PAIRS_INFO}
          />
          <StatBox
            label="Agreed non-detections"
            value={`${pair.matchedNonDetectionPercent.toFixed(0)}%`}
            sub={`${pair.matchedNonDetectionPairs} sample-assay`}
            muted
            info={AGREED_NON_DETECTION_INFO_PAIR}
          />
          <StatBox
            label="QA mismatch"
            value={`${pair.qaMismatchPercent.toFixed(1)}%`}
            sub={`${pair.qaMismatchPairs} sample-assay`}
            info={QA_MISMATCH_INFO_PAIR}
          />
          <StatBox
            label="Consensus MAD"
            value={`${pair.consensusMad.toFixed(2)} Cq`}
            sub={`${pair.madPairs} sample-assay`}
            info={CONSENSUS_MAD_INFO_PAIR}
          />
        </HStack>
      </SectionCard>

      <SectionCard>
        <Heading as="h4" size="sm" mb={4}>
          Bland–Altman plot
        </Heading>
        <HStack spacing={4} mb={4} align="flex-start">
          <StatBox
            label="LoA width"
            value={`${(2 * 1.96 * blandAltman.sdDelta).toFixed(2)} Cq`}
            sub="95% limits of agreement"
            muted
            info={LOA_WIDTH_INFO}
          />
        </HStack>
        <Box ref={plotContainerRef} width="100%">
          <BlandAltmanPlot
            width={containerWidth ?? 700}
            height={320}
            data={blandAltman.points.map(p => ({ x: p.mean, y: p.delta }))}
            meanDelta={blandAltman.meanDelta}
            sdDelta={blandAltman.sdDelta}
            loaLower={blandAltman.loaLower}
            loaUpper={blandAltman.loaUpper}
          />
        </Box>
      </SectionCard>

      <CollapsibleSection title="Secondary diagnostics (bias, spread, systematic shift)">
        <VStack align="stretch" spacing={2}>
          <Text fontSize="sm">mean(ΔCq) = {blandAltman.meanDelta.toFixed(2)} — systematic shift / bias</Text>
          <Text fontSize="sm">
            RMS(ΔCq) = {blandAltman.rmsDelta.toFixed(2)} — same, but weights large outlier differences
          </Text>
          <Text fontSize="sm">
            Within |ΔCq| ≤ 2 ➝ {blandAltman.withinTolerancePercent.toFixed(1)}% — reflects both bias and magnitude of
            disagreement regardless of direction.
          </Text>
          <Text fontSize="sm">
            ||mean(d)|| / √k = {Math.sqrt(pair.statistic).toPrecision(3)} — aggregates same-direction per-assay shifts
            without letting opposite-direction shifts cancel out.
          </Text>
          <Text fontSize="sm">
            Sign-flip p-value = {pair.pValue.toFixed(3)} — likelihood of a systematic shift this large under the null of
            no shift; statistic ||mean(d)||²/k = {pair.statistic.toPrecision(3)}.
          </Text>
        </VStack>
      </CollapsibleSection>
    </VStack>
  );
}

// ---------------------------------------------------------------------------------------------------------------------
// Resolved samples grid (read-only; used in chips mode to show the auto-paired samples per chip)
// ---------------------------------------------------------------------------------------------------------------------

type ResolvedRow = Record<string, string>;

function ResolvedSamplesGrid({ block }: { block: ReproResolvedBlock }) {
  const columnDefs = useMemo<Array<ColGroupDef<ResolvedRow>>>(
    () =>
      block.setLabels.map((label, si) => ({
        headerName: label || `Set ${si + 1}`,
        groupId: `grp_${si}`,
        children: [
          { headerName: 'Sample id', field: `s${si}_id`, colId: `s${si}_id`, flex: 1, minWidth: 110 },
          {
            headerName: 'Sampling point',
            field: `s${si}_point`,
            colId: `s${si}_point`,
            flex: 1,
            minWidth: 140,
            cellStyle: READONLY_META_CELL_STYLE,
          },
          {
            headerName: 'Date',
            field: `s${si}_date`,
            colId: `s${si}_date`,
            flex: 1,
            minWidth: 120,
            cellStyle: READONLY_META_CELL_STYLE,
          },
        ],
      })),
    [block.setLabels],
  );

  const rowData = useMemo<ResolvedRow[]>(
    () =>
      block.sampleGroups.map((group, gi) => {
        const row: ResolvedRow = { _rowId: String(gi) };
        group.cells.forEach((cell, si) => {
          row[`s${si}_id`] = cell.sampleId != null ? String(cell.sampleId) : '';
          row[`s${si}_point`] = cell.samplingPointName ?? '';
          row[`s${si}_date`] = cell.sampleId != null ? timeToIsoDate(cell.time) : '';
        });
        return row;
      }),
    [block.sampleGroups],
  );

  const defaultColDef = useMemo<ColDef<ResolvedRow>>(
    () => ({ editable: false, resizable: true, sortable: false, filter: false }),
    [],
  );

  return (
    <Box borderWidth="1px" borderRadius="md" overflow="hidden">
      <Box style={{ width: '100%', height: '340px' }}>
        <AgGridReact<ResolvedRow>
          rowData={rowData}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          getRowId={params => params.data._rowId}
          theme={themeQuartz}
          rowHeight={30}
          headerHeight={36}
          suppressContextMenu
        />
      </Box>
    </Box>
  );
}

// ---------------------------------------------------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------------------------------------------------

function moveItem<T>(items: T[], predicate: (item: T) => boolean, direction: -1 | 1): T[] {
  const index = items.findIndex(predicate);
  if (index < 0) return items;
  const target = index + direction;
  if (target < 0 || target >= items.length) return items;
  const next = [...items];
  [next[index], next[target]] = [next[target], next[index]];
  return next;
}

function nextSetLabel(setColumns: SetColumn[]): string {
  const used = new Set(setColumns.map(col => col.label));
  for (let i = 0; i < 26; i++) {
    const label = String.fromCharCode(65 + i);
    if (!used.has(label)) return label;
  }
  return `Set ${setColumns.length + 1}`;
}
